/* eslint-disable no-console */
import React, { useCallback, useEffect, useRef } from 'react';

import { createContextHook } from 'use-context-hook';

import { getBrokerCredentials } from '../services/runtimeConfigService';

export const MqttContext = createContextHook();

export function MQTTContextProvider(props) {
  const connRef = useRef(null);
  const [resub, setResub] = React.useState(false);

  const onMessage = useCallback(_ => {
    const msg = JSON.parse(_.payloadString);
    if (
      msg &&
      (_.destinationName.split('/').pop().includes('status_') ||
        _.destinationName.split('/').pop().includes('terminals_'))
    ) {
      window.dispatchEvent(new CustomEvent('terminal_update', { detail: msg }));
    } else {
      const event = _.destinationName.split('/').pop();

      window.dispatchEvent(new CustomEvent(event, { detail: msg }));
    }
  }, []);

  const LeaveRoom = () => {
    if (!connRef.current?.isConnected?.()) {
      return false;
    }
    try {
      connRef.current.unsubscribe('server_update', {
        onSuccess: () => {
          // console.log('Unsubscribed from server_update');
        },
        onFailure: error => {
          console.log(`Could not unsubscribe from server_update: ${error.errorMessage}`);
        },
      });
      connRef.current.unsubscribe('clerk_update', {
        onSuccess: () => {
          // console.log('Unsubscribed from clerk_update');
        },
        onFailure: error => {
          console.log(`Could not unsubscribe from clerk_update: ${error.errorMessage}`);
        },
      });
      connRef.current.unsubscribe('manager_update', {
        onSuccess: () => {
          // console.log('Unsubscribed from manager_update');
        },
        onFailure: error => {
          console.log(`Could not unsubscribe from manager_update: ${error.errorMessage}`);
        },
      });
      connRef.current.unsubscribe('owner_update', {
        onSuccess: () => {
          // console.log('Unsubscribed from owner_update');
        },
        onFailure: error => {
          console.log(`Could not unsubscribe from owner_update: ${error.errorMessage}`);
        },
      });
      connRef.current.unsubscribe('merchant_tools_bull_progress', {
        onSuccess: () => {
          // console.log('Unsubscribed from merchant_tools_bull_progress');
        },
        onFailure: error => {
          console.log(`Could not unsubscribe from merchant_tools_bull_progress: ${error.errorMessage}`);
        },
      });
      return true;
    } catch (error) {
      console.log(`LeaveRoom failed: ${error?.message || String(error)}`);
      return false;
    }
  };
  const JoinRoom = () => {
    if (!connRef.current?.isConnected?.()) {
      return false;
    }
    try {
      connRef.current.subscribe('server_update', {
        qos: 2,
        onSuccess: () => {
          // console.log('Subscribed to server_update');
        },
        onFailure: error => {
          console.log(`Could not subscribe to server_update: ${error.errorMessage}`);
        },
      });
      connRef.current.subscribe('clerk_update', {
        qos: 2,
        onSuccess: () => {
          // console.log('Subscribed to clerk_update');
        },
        onFailure: error => {
          console.log(`Could not subscribe to clerk_update: ${error.errorMessage}`);
        },
      });
      connRef.current.subscribe('manager_update', {
        qos: 2,
        onSuccess: () => {
          // console.log('Subscribed to manager_update');
        },
        onFailure: error => {
          console.log(`Could not subscribe to manager_update: ${error.errorMessage}`);
        },
      });
      connRef.current.subscribe('owner_update', {
        qos: 2,
        onSuccess: () => {
          // console.log('Subscribed to owner_update');
        },
        onFailure: error => {
          console.log(`Could not subscribe to owner_update: ${error.errorMessage}`);
        },
      });
      connRef.current.subscribe('merchant_tools_bull_progress', {
        qos: 2,
        onSuccess: () => {
          // console.log('Subscribed to merchant_tools_bull_progress');
        },
        onFailure: error => {
          console.log(`Could not subscribe to merchant_tools_bull_progress: ${error.errorMessage}`);
        },
      });
      return true;
    } catch (error) {
      console.log(`JoinRoom failed: ${error?.message || String(error)}`);
      return false;
    }
  };

  function subscribeWithOptions(topic, options = {}) {
    const client = connRef.current;
    if (!client || typeof client.isConnected !== 'function' || !client.isConnected()) {
      return false;
    }

    try {
      const onSuccess = () => {
        if (!options?.onSuccess) {
          console.log(`Subscribed to ${topic}`);
        }
        options?.onSuccess?.();
      };

      const onFailure = error => {
        const msg = error?.errorMessage || error?.message || String(error);
        console.log(`Could not subscribe to ${topic}: ${msg}`);
        options?.onFailure?.(error);
      };

      client.subscribe(topic, {
        qos: options?.qos ?? 2,
        ...options,
        onSuccess,
        onFailure,
      });
      return true;
    } catch (error) {
      const msg = error?.message || String(error);
      console.log(`Could not subscribe to ${topic}: ${msg}`);
      options?.onFailure?.(error);
      return false;
    }
  }

  const unsubscribe = (topic, options = {}) => {
    const client = connRef.current;
    if (!client || typeof client.isConnected !== 'function' || !client.isConnected()) {
      return false;
    }

    try {
      const onSuccess = () => {
        if (!options?.onSuccess) {
          console.log(`Unsubscribed from ${topic}`);
        }
        options?.onSuccess?.();
      };

      const onFailure = error => {
        const msg = error?.errorMessage || error?.message || String(error);
        console.log(`Could not unsubscribe from ${topic}: ${msg}`);
        options?.onFailure?.(error);
      };

      client.unsubscribe(topic, {
        ...options,
        onSuccess,
        onFailure,
      });
      return true;
    } catch (error) {
      const msg = error?.message || String(error);
      console.log(`Could not unsubscribe from ${topic}: ${msg}`);
      options?.onFailure?.(error);
      return false;
    }
  };

  const subscribe = (topic, options = {}) => subscribeWithOptions(topic, options);

  const renewTimerRef = useRef(null);

  const renewingRef = useRef(false);

  const connection = useCallback(async () => {
    let credentials;
    try {
      credentials = await getBrokerCredentials();
    } catch (error) {
      console.log(`Could not obtain MQTT credentials: ${error.message}`);

      renewTimerRef.current = setTimeout(() => connection(), 30_000);
      return;
    }

    if (!connRef.current) {
      connRef.current = new window.Paho.MQTT.Client(credentials.url, new Date().getTime().toString());
      connRef.current.onMessageArrived = onMessage;
      connRef.current.onConnectionLost = response => {
        if (renewingRef.current) return;
        console.log(`Connection lost: ${response.errorMessage}`);
        connection();
      };
    }

    connRef.current.connect({
      userName: credentials.username,
      password: credentials.password,
      onSuccess: () => {
        console.log('Connected to MQTT Broker');
        setResub(prev => !prev);
        JoinRoom();

        clearTimeout(renewTimerRef.current);

        const ttlSeconds = Number(credentials.ttlSeconds);
        const renewIn = Number.isFinite(ttlSeconds) ? Math.max((ttlSeconds - 60) * 1000, 30_000) : 30_000;

        renewTimerRef.current = setTimeout(() => {
          renewingRef.current = true;
          try {
            if (connRef.current?.isConnected?.()) connRef.current.disconnect();
          } catch (error) {
            console.log(`Could not close MQTT connection before renewal: ${error.message}`);
          }
          connection().finally(() => {
            renewingRef.current = false;
          });
        }, renewIn);
      },
      onFailure: error => {
        console.log(`Could not connect to MQTT Broker: ${error.errorMessage}`);
      },
    });
  }, [onMessage]);

  useEffect(() => {
    if (typeof window === 'undefined' || connRef.current) return undefined;
    connection();
    return () => clearTimeout(renewTimerRef.current);
  }, []);

  const _con = React.useMemo(
    () => ({
      JoinRoom,
      LeaveRoom,
      subscribe,
      unsubscribe,
      resub,
      connRef,
    }),
    [JoinRoom, LeaveRoom],
  );
  return <MqttContext.Provider value={_con}>{props.children}</MqttContext.Provider>;
}
